import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, X, Paintbrush, ChevronDown, ChevronRight, Copy,
  CheckCircle2, XCircle, Archive, Image,
} from "lucide-react";

const TYPES = ["print", "embroidery", "woven", "applique"];
const TYPE_LABELS = { print: "Print", embroidery: "Embroidery", woven: "Woven", applique: "Applique" };
const STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  archived: "bg-amber-100 text-amber-800",
};

const EMPTY_FORM = {
  name: "",
  season: "",
  artwork_type: "print",
  repeat_width_cm: "",
  repeat_height_cm: "",
  mill_vendor_id: "",
  design_file_name: "",
  design_file_url: "",
  sep_name: "",
  sep_hex: "",
};

export default function ArtworkManagement() {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showClone, setShowClone] = useState(null);
  const [cloneSeason, setCloneSeason] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formFiles, setFormFiles] = useState([]);
  const [formSeps, setFormSeps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [strikeOffForm, setStrikeOffForm] = useState({ artworkId: null, notes: "" });
  const [outcomeForm, setOutcomeForm] = useState({ artworkId: null, roundNumber: null, outcome: "approved", notes: "" });
  const [suppliers, setSuppliers] = useState([]);

  const load = () => {
    api.get("/artworks")
      .then((r) => setArtworks(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        season: form.season,
        artwork_type: form.artwork_type,
        design_files: formFiles,
        colour_separations: formSeps,
        repeat_width_cm: form.repeat_width_cm ? parseFloat(form.repeat_width_cm) : null,
        repeat_height_cm: form.repeat_height_cm ? parseFloat(form.repeat_height_cm) : null,
        mill_vendor_id: form.mill_vendor_id || null,
      };
      await api.post("/artworks", payload);
      toast.success("Artwork created");
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      setFormFiles([]);
      setFormSeps([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/artworks/${id}`, { status });
      toast.success(`Artwork ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addDesignFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setFormFiles([...formFiles, { name: file.name, url: reader.result, type: file.type }]);
    };
    reader.readAsDataURL(file);
  };

  const addFormSep = () => {
    if (!form.sep_name.trim()) return;
    setFormSeps([...formSeps, { name: form.sep_name, hex: form.sep_hex || "#000000" }]);
    setForm({ ...form, sep_name: "", sep_hex: "" });
  };

  const addStrikeOffRound = async (artworkId) => {
    const artwork = artworks.find((a) => a.id === artworkId);
    if (!artwork) return;
    const rounds = [...(artwork.strike_off_rounds || []), { round: (artwork.strike_off_rounds || []).length + 1, status: "pending", notes: strikeOffForm.notes }];
    try {
      await api.patch(`/artworks/${artworkId}`, { strike_off_rounds: rounds });
      toast.success("Strike-off round added");
      setStrikeOffForm({ artworkId: null, notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateSeparations = async (artworkId, seps) => {
    try {
      await api.patch(`/artworks/${artworkId}`, { colour_separations: seps });
      toast.success("Colour separations updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitStrikeOffOutcome = async () => {
    if (!outcomeForm.artworkId || !outcomeForm.roundNumber) return;
    try {
      await api.post(`/artworks/${outcomeForm.artworkId}/strike-off-rounds/${outcomeForm.roundNumber}/outcome`, {
        outcome: outcomeForm.outcome,
        notes: outcomeForm.notes,
      });
      toast.success(`Strike-off round ${outcomeForm.outcome}`);
      setOutcomeForm({ artworkId: null, roundNumber: null, outcome: "approved", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cloneArtwork = async (id) => {
    if (!cloneSeason.trim()) return;
    try {
      await api.post(`/artworks/${id}/clone`, { new_season: cloneSeason });
      toast.success("Artwork cloned");
      setShowClone(null);
      setCloneSeason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = activeType === "all" ? artworks : artworks.filter((a) => a.artwork_type === activeType);
  const typeCounts = TYPES.reduce((acc, t) => { acc[t] = artworks.filter((a) => a.artwork_type === t).length; return acc; }, {});

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Artwork, Print & Embroidery</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage artwork designs, strike-off rounds and colour separations.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New artwork
        </button>
      </div>

      {/* Type filter tabs */}
      <div className="mt-8 flex gap-2 overflow-x-auto pb-2">
        <button onClick={() => setActiveType("all")}
          className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${activeType === "all" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
          All ({artworks.length})
        </button>
        {TYPES.map((t) => (
          <button key={t} onClick={() => setActiveType(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${activeType === t ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            {TYPE_LABELS[t]} ({typeCounts[t]})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Paintbrush size={26} className="mx-auto mb-3" />
          <p className="text-sm">No artworks yet.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((a) => (
            <div key={a.id} className="border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
              {/* Thumbnail */}
              <div className="h-32 bg-zinc-100 flex items-center justify-center relative">
                {a.design_files && a.design_files.length > 0 && a.design_files[0].url ? (
                  <img src={a.design_files[0].url} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <Image size={28} className="text-zinc-300" />
                )}
                <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[a.status] || STATUS_STYLE.pending}`}>
                  {a.status}
                </span>
              </div>

              {/* Info */}
              <div className="p-4">
                <p className="text-sm font-medium truncate">{a.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-medium">{TYPE_LABELS[a.artwork_type] || a.artwork_type}</span>
                  {a.season && <span className="text-xs text-zinc-400">{a.season}</span>}
                </div>
                {a.mill_vendor_name && (
                  <p className="text-xs text-zinc-500 mt-1 truncate" title={a.mill_vendor_name}>
                    Vendor: {a.mill_vendor_name}
                  </p>
                )}
                {(a.repeat_width_cm || a.repeat_height_cm) && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Repeat: {a.repeat_width_cm || "—"} x {a.repeat_height_cm || "—"} cm
                  </p>
                )}
                {a.cloned_from_id && (
                  <p className="text-[10px] text-zinc-400 mt-1">Cloned</p>
                )}
              </div>

              {/* Expand / Actions toggle */}
              <div className="border-t border-zinc-100">
                <button onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-zinc-500 hover:bg-zinc-50 transition-colors">
                  <span>Details & Actions</span>
                  {expandedId === a.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
              </div>

              {/* Expanded detail */}
              {expandedId === a.id && (
                <div className="px-4 pb-4 border-t border-zinc-100 pt-3 space-y-4">
                  {/* Placement preview */}
                  {a.design_files && a.design_files.length > 0 && a.artwork_type === "print" && (
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">Placement Preview</h4>
                      <div className="relative w-full h-40 bg-zinc-50 rounded-md overflow-hidden border border-zinc-200">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `url(${a.design_files[0].url})`,
                          backgroundSize: `${a.repeat_width_cm ? Math.min(a.repeat_width_cm * 3, 100) : 60}px`,
                          backgroundRepeat: "repeat",
                          opacity: 0.85,
                        }} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white/80 px-3 py-1 rounded text-[10px] text-zinc-500 font-medium">
                            Tiled repeat preview
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Design files gallery */}
                  {a.design_files && a.design_files.length > 1 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">Design Files ({a.design_files.length})</h4>
                      <div className="flex gap-2 overflow-x-auto">
                        {a.design_files.map((f, i) => (
                          <div key={i} className="w-16 h-16 flex-shrink-0 bg-zinc-100 rounded overflow-hidden border border-zinc-200">
                            {f.url ? (
                              <img src={f.url} alt={f.name || `File ${i + 1}`} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-zinc-300"><Image size={16} /></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vendor info */}
                  {a.mill_vendor_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-400">Mill / Vendor:</span>
                      <span className="font-medium text-zinc-700">{a.mill_vendor_name}</span>
                    </div>
                  )}

                  {/* Strike-off rounds */}
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">Strike-off Rounds</h4>
                    {(a.strike_off_rounds || []).length === 0 ? (
                      <p className="text-xs text-zinc-400">No rounds recorded.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {(a.strike_off_rounds || []).map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-400 w-14">Round {r.round || i + 1}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                              {r.status || "pending"}
                            </span>
                            {r.notes && <span className="text-zinc-500 truncate">{r.notes}</span>}
                            {(r.status === "pending" || !r.status) && (
                              <div className="flex gap-1 ml-auto">
                                {outcomeForm.artworkId === a.id && outcomeForm.roundNumber === (r.round || i + 1) ? (
                                  <>
                                    <select value={outcomeForm.outcome} onChange={(e) => setOutcomeForm({ ...outcomeForm, outcome: e.target.value })}
                                      className="border border-zinc-300 rounded text-[10px] px-1 py-0.5">
                                      <option value="approved">Approved</option>
                                      <option value="rejected">Rejected</option>
                                      <option value="revision">Revision</option>
                                    </select>
                                    <button onClick={submitStrikeOffOutcome}
                                      className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded text-[10px] hover:bg-emerald-100">Submit</button>
                                    <button onClick={() => setOutcomeForm({ artworkId: null, roundNumber: null, outcome: "approved", notes: "" })}
                                      className="text-[10px] text-zinc-400">Cancel</button>
                                  </>
                                ) : (
                                  <button onClick={() => setOutcomeForm({ artworkId: a.id, roundNumber: r.round || i + 1, outcome: "approved", notes: "" })}
                                    className="px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded text-[10px] hover:bg-zinc-200">Decide</button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Add strike-off round */}
                    {strikeOffForm.artworkId === a.id ? (
                      <div className="mt-2 flex gap-2">
                        <input value={strikeOffForm.notes} onChange={(e) => setStrikeOffForm({ ...strikeOffForm, notes: e.target.value })} placeholder="Round notes..."
                          className="flex-1 border border-zinc-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950" />
                        <button onClick={() => addStrikeOffRound(a.id)}
                          className="px-2 py-1 bg-zinc-950 text-white rounded text-xs hover:bg-zinc-800">Add</button>
                        <button onClick={() => setStrikeOffForm({ artworkId: null, notes: "" })} className="text-xs text-zinc-400">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setStrikeOffForm({ artworkId: a.id, notes: "" })}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
                        <Plus size={12} /> New strike-off round
                      </button>
                    )}
                  </div>

                  {/* Colour separations editor */}
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">Colour Separations</h4>
                    {(a.colour_separations || []).length === 0 ? (
                      <p className="text-xs text-zinc-400">No separations defined.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(a.colour_separations || []).map((cs, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1">
                            {cs.hex && <span className="w-3 h-3 rounded-full border border-zinc-200" style={{ backgroundColor: cs.hex }} />}
                            {cs.name || `Sep ${i + 1}`}
                            <button onClick={() => updateSeparations(a.id, (a.colour_separations || []).filter((_, j) => j !== i))}
                              className="text-zinc-400 hover:text-red-500 ml-0.5">&times;</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
                    {a.status !== "approved" && (
                      <button onClick={() => updateStatus(a.id, "approved")}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100">
                        <CheckCircle2 size={12} /> Approve
                      </button>
                    )}
                    {a.status !== "rejected" && (
                      <button onClick={() => updateStatus(a.id, "rejected")}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-md text-xs font-medium hover:bg-red-100">
                        <XCircle size={12} /> Reject
                      </button>
                    )}
                    {a.status !== "archived" && (
                      <button onClick={() => updateStatus(a.id, "archived")}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-100">
                        <Archive size={12} /> Archive
                      </button>
                    )}
                    <button onClick={() => { setShowClone(a.id); setCloneSeason(""); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-md text-xs font-medium hover:bg-zinc-200">
                      <Copy size={12} /> Clone
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New artwork</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Artwork name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="Season (e.g. AW25)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={form.artwork_type} onChange={(e) => setForm({ ...form, artwork_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.repeat_width_cm} onChange={(e) => setForm({ ...form, repeat_width_cm: e.target.value })} placeholder="Repeat width (cm)" type="number" step="0.1"
                  className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <input value={form.repeat_height_cm} onChange={(e) => setForm({ ...form, repeat_height_cm: e.target.value })} placeholder="Repeat height (cm)" type="number" step="0.1"
                  className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <select value={form.mill_vendor_id} onChange={(e) => setForm({ ...form, mill_vendor_id: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select mill / vendor (optional)</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.company_name}{s.country ? ` (${s.country})` : ""}</option>
                ))}
              </select>
              {/* Design files upload */}
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Design files</label>
                <input type="file" accept="image/*,.ai,.psd,.pdf" multiple onChange={(e) => { Array.from(e.target.files).forEach(addDesignFile); }}
                  className="w-full text-sm text-zinc-600 file:mr-3 file:py-1.5 file:px-3 file:border file:border-zinc-300 file:rounded-md file:text-xs file:bg-white file:text-zinc-700 hover:file:bg-zinc-50" />
                {formFiles.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {formFiles.map((f, i) => (
                      <span key={i} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded flex items-center gap-1">
                        {f.name}
                        <button onClick={() => setFormFiles(formFiles.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-zinc-700">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* Colour separations */}
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Colour separations</label>
                <div className="flex gap-2">
                  <input value={form.sep_name} onChange={(e) => setForm({ ...form, sep_name: e.target.value })} placeholder="Name (e.g. Pantone 185C)"
                    className="flex-1 border border-zinc-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  <input type="color" value={form.sep_hex || "#000000"} onChange={(e) => setForm({ ...form, sep_hex: e.target.value })}
                    className="w-10 h-9 border border-zinc-300 rounded-md cursor-pointer" />
                  <button type="button" onClick={addFormSep} className="px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-md text-xs hover:bg-zinc-200">Add</button>
                </div>
                {formSeps.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {formSeps.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-zinc-50 border border-zinc-200 rounded px-2 py-0.5">
                        <span className="w-3 h-3 rounded-full border border-zinc-200" style={{ backgroundColor: s.hex }} />
                        {s.name}
                        <button onClick={() => setFormSeps(formSeps.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-zinc-700">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={create} disabled={!form.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create artwork"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone dialog */}
      {showClone && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowClone(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Clone artwork</h2>
              <button onClick={() => setShowClone(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-zinc-500">Clone this artwork into a new season. All design files and colour separations will be copied.</p>
              <input value={cloneSeason} onChange={(e) => setCloneSeason(e.target.value)} placeholder="New season (e.g. SS26)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={() => cloneArtwork(showClone)} disabled={!cloneSeason.trim()}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                Clone artwork
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
